import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import { Menu } from '@headlessui/react';
import {
  EllipsisVerticalIcon,
  XMarkIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  CurrencyRupeeIcon,
  ChevronDownIcon,
  CalendarIcon,
  FunnelIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  UserIcon,
  CheckIcon,
  XCircleIcon,
  BanknotesIcon,
  WalletIcon,
  ArrowLeftIcon,
} from '@heroicons/react/24/outline';
import { TbFilterPause } from "react-icons/tb";
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import sessionCheck from "../session/userData";
import { encryptDataHybrid } from "../enc/useEncrypt";
import { useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { getDeviceInfo } from '../utils/deviceInfo';
import { motion, AnimatePresence } from 'framer-motion';
import { Switch } from '@headlessui/react';

const API_URL = process.env.REACT_APP_API_URL;

const Users = ({ children }) => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const navigate = useNavigate();
  const [cardInfo, setCardInfo] = useState([]);
  const [access_data, setAccess_data] = useState([]);

  useEffect(() => {
    if (sessionCheck() == null) {
      navigate("../login", { replace: true });
    } else {
      setAccess_data(JSON.parse(sessionCheck()));
    }
  }, []);

  useEffect(() => {
    if (access_data?.username) {
      fetchReport();
    }
  }, [access_data?.username]);

  useEffect(() => {
    if (filterDrawerOpen) {
      // disable scrolling
      document.body.style.overflow = "hidden";
    } else {
      // enable scrolling again
      document.body.style.overflow = "auto";
    }

    // cleanup when component unmounts
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [filterDrawerOpen]);

  // Pagination state
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10
  });

  const handleStatusChange = async (userId, newActiveStatus) => {
    const newStatus = newActiveStatus ? 'Active' : 'Deactive';

    setUsers(prevUsers =>
      prevUsers.map(user =>
        user.id === userId ? { ...user, status: newStatus } : user
      )
    );

    try {
      const response = await updateUserStatus(userId, newStatus);
      if (!response) throw new Error('Update failed');

      setFilteredUsers(prev =>
        prev.map(user =>
          user.id === userId ? { ...user, status: newStatus } : user
        )
      );

      toast.success(`Status updated to ${newStatus}`);
    } catch (error) {
      setUsers(prevUsers =>
        prevUsers.map(user =>
          user.id === userId ? { ...user, status: user.status === 'Active' ? 'Deactive' : 'Active' } : user
        )
      );
      setFilteredUsers(prev =>
        prev.map(user =>
          user.id === userId ? { ...user, status: user.status === 'Active' ? 'Deactive' : 'Active' } : user
        )
      );
      toast.error(error.message);
    }
  };

  const updateUserStatus = async (userId, newStatus) => {
    try {
      const dataToEncrypt = {
        user_id: access_data?.username || '',
        login_id: access_data?.login_id || '',
        device_id: getDeviceInfo().deviceId,
        payload: {
          query: 'status-update',
          UserId: userId,
          newStatus: newStatus
        }
      };

      const encryptedMessage = encryptDataHybrid(dataToEncrypt);
      if (!encryptedMessage) {
        throw new Error("Encryption failed");
      }

      const response = await fetch(`${API_URL}user-status-update.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${Math.floor(Date.now() / 1000) * 784116}`,
        },
        body: JSON.stringify({
          encrypted_key: encryptedMessage.encryptedKey,
          encrypted_data: encryptedMessage.encryptedData,
        }),
      });

      const result = await response.json();
      return result.status === true;
    } catch (error) {
      console.error('Status update error:', error);
      throw error;
    }
  };

  const [filters, setFilters] = useState({
    dateRange: 'Today',
    startDate: new Date(),
    endDate: new Date(),
    status: '',
    name: '',
    minWallet: '',
    maxWallet: '',
    mobileNumber: '',
    UserId: '',
    eKYC: '',
  });

  const dateRangeOptions = ['Today', 'This Week', 'This Month', 'Last Month', 'Custom Range'];
  const statusOptions = ['All', 'Active', 'Deactive'];
  const yesNoOptions = ['All', 'Yes', 'No'];

  const applyFilters = () => {
    fetchReport(filters);
    setFilterDrawerOpen(false);
  };

  const resetFilters = () => {
    const defaultFilters = {
      dateRange: 'Today',
      startDate: new Date(),
      endDate: new Date(),
      status: '',
      name: '',
      minWallet: '',
      maxWallet: '',
      mobileNumber: '',
      UserId: '',
      eKYC: '',
    };

    setFilters(defaultFilters);
    fetchReport(defaultFilters);
    setFilterDrawerOpen(false);
  };

  const handleFilterChange = (name, value) => {
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDateRangeChange = (range) => {
    const today = new Date();
    let startDate, endDate = today;

    switch (range) {
      case 'Today':
        startDate = today;
        break;
      case 'This Week':
        startDate = new Date(today.setDate(today.getDate() - today.getDay()));
        break;
      case 'This Month':
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        break;
      case 'Last Month':
        startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        endDate = new Date(today.getFullYear(), today.getMonth(), 0);
        break;
      default:
        toast.info(filters.startDate);
        startDate = filters.startDate;
        endDate = filters.endDate;
    }

    setFilters(prev => ({
      ...prev,
      dateRange: range,
      startDate,
      endDate
    }));
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination(prev => ({ ...prev, currentPage: newPage }));
    }
  };

  const getPaginatedData = () => {
    const startIndex = (pagination.currentPage - 1) * pagination.itemsPerPage;
    const endIndex = startIndex + pagination.itemsPerPage;
    return filteredUsers.slice(startIndex, endIndex);
  };


  const cardData = [
    {
      title: 'Total Users',
      amount: `${cardInfo.total_users || 0}`,
      icon: <UserIcon className="h-6 w-6 text-white" />,
      color: 'bg-blue-500',
    },
    {
      title: 'Active Users',
      amount: `${cardInfo.active_users || 0}`,
      icon: <CheckIcon className="h-6 w-6 text-white" />,
      color: 'bg-green-500',
    },
    {
      title: 'Block Users',
      amount: `${cardInfo.blocked_user || 0}`,
      icon: <XCircleIcon className="h-6 w-6 text-white" />,
      color: 'bg-red-500',
    },
    {
      title: 'User Balance',
      amount: `₹${(parseFloat(cardInfo.user_balance) || 0).toFixed(2)}`,
      icon: <CurrencyRupeeIcon className="h-6 w-6 text-white" />,
      color: 'bg-indigo-500',
    },
  ];

  const fetchReport = async (filterParams = null) => {
    setLoading(true);

    try {
      const activeFilters = filterParams || filters;

      const dataToEncrypt = {
        user_id: access_data?.username || '',
        login_id: access_data?.login_id || '',
        device_id: getDeviceInfo().deviceId,
        payload: {
          query: 'users',
          dateRange: activeFilters.dateRange,
          fromDate: activeFilters.startDate.toISOString().split('T')[0],
          toDate: activeFilters.endDate.toISOString().split('T')[0],
          status: activeFilters.status,
          name: activeFilters.name,
          minWallet: activeFilters.minWallet,
          maxWallet: activeFilters.maxWallet,
          mobileNumber: activeFilters.mobileNumber,
          UserId: activeFilters.UserId,
          eKYC: activeFilters.eKYC === 'Yes' ? true : activeFilters.eKYC === 'No' ? false : null,
        }
      };

      console.log(dataToEncrypt);

      const encryptedMessage = encryptDataHybrid(dataToEncrypt);

      if (!encryptedMessage) {
        toast.error("Encryption failed. Please try again.");
        setLoading(false);
        return;
      }

      const payloadToSend = {
        encrypted_key: encryptedMessage.encryptedKey,
        encrypted_data: encryptedMessage.encryptedData,
      };

      const response = await fetch(`${API_URL}user.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${Math.floor(Date.now() / 1000) * 784116}`,
        },
        body: JSON.stringify(payloadToSend),
      });

      const result = await response.json();

      if (result.status === true) {
        setCardInfo(result.dashboard);

        const apiUsers = result.data.map(item => ({
          id: item.user_id,
          name: item.name || 'N/A',
          email: item.email || 'N/A',
          mobile: item.mobile || 'N/A',
          status: item.status === "1" ? 'Active' : 'Deactive',
          walletBalance: `${parseFloat(item.balance || 0).toFixed(2)}`,
          joiningDate: item.createAt || 'N/A',
          eKYC: item.iskyc ? 'Yes' : 'No' || 'No',
          referCode: item.refer_code || 'N/A',
          planId: item.plan_id || 'N/A',
          address: item.address || 'N/A'
        }));

        setUsers(apiUsers);
        setFilteredUsers(apiUsers);

        setPagination(prev => ({
          ...prev,
          currentPage: 1,
          totalItems: apiUsers.length,
          totalPages: Math.ceil(apiUsers.length / prev.itemsPerPage)
        }));
      } else {
        throw new Error(result.message || "Failed to fetch User data.");
      }
    } catch (error) {
      setUsers([]);
      setFilteredUsers([]);
      console.error("Fetch User error:", error);
      toast.error(error.message || "An unexpected error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setFilteredUsers(users);
  }, [users]);

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 dark:bg-gray-900 dark:text-white">
      <div className="flex flex-col flex-1">
        <Header />
        <main className="flex-1 overflow-auto">
          {/* Filter Drawer */}
          <AnimatePresence>
            {filterDrawerOpen && (
              <motion.div
                initial={{ x: '-100%' }}
                animate={{ x: 0 }}
                exit={{ x: '-100%' }}
                transition={{ type: 'tween' }}
                className="fixed inset-y-0 left-0 z-50 w-80 bg-white dark:bg-gray-800 shadow-lg overflow-y-auto"
              >
                <div className="p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-medium">Filter Users</h2>
                    <button
                      onClick={() => setFilterDrawerOpen(false)}
                      className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700"
                    >
                      <XMarkIcon className="h-5 w-5 text-gray-600 dark:text-gray-300" />
                    </button>
                  </div>

                  <div className="space-y-4">
                    {/* Date Range */}
                    <div className="space-y-1">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Joining Date</label>
                      <Menu as="div" className="relative">
                        <Menu.Button className="w-full flex justify-between items-center px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-left text-sm focus:outline-none">
                          <span>{filters.dateRange}</span>
                          <ChevronDownIcon className="h-4 w-4" />
                        </Menu.Button>
                        <Menu.Items className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-700 shadow-lg rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 focus:outline-none">
                          {dateRangeOptions.map((option) => (
                            <Menu.Item key={option}>
                              {({ active }) => (
                                <button
                                  onClick={() => handleDateRangeChange(option)}
                                  className={`${active ? 'bg-gray-100 dark:bg-gray-600' : ''} block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200`}
                                >
                                  {option}
                                </button>
                              )}
                            </Menu.Item>
                          ))}
                        </Menu.Items>
                      </Menu>
                      {filters.dateRange === 'Custom Range' && (
                        <div className="grid grid-cols-2 gap-2 mt-2">
                          <div>
                            <DatePicker
                              selected={filters.startDate}
                              onChange={(date) => handleFilterChange('startDate', date)}
                              selectsStart
                              startDate={filters.startDate}
                              endDate={filters.endDate}
                              popperPlacement="bottom-start"
                              popperClassName="z-50" // ensures above drawer
                              popperProps={{
                                modifiers: [
                                  {
                                    name: "preventOverflow",
                                    options: {
                                      boundary: "viewport", // keeps inside screen
                                    },
                                  },
                                ],
                              }}
                              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm bg-white dark:bg-gray-700"
                            />
                          </div>
                          <div>
                            <DatePicker
                              selected={filters.endDate}
                              onChange={(date) => handleFilterChange('endDate', date)}
                              selectsEnd
                              startDate={filters.startDate}
                              endDate={filters.endDate}
                              minDate={filters.startDate}
                              popperPlacement="bottom-end"
                              popperClassName="z-50"
                              popperProps={{
                                modifiers: [
                                  {
                                    name: "preventOverflow",
                                    options: {
                                      boundary: "viewport",
                                    },
                                  },
                                ],
                              }}
                              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm bg-white dark:bg-gray-700"
                            />
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Status */}
                    <div className="space-y-1">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Status</label>
                      <Menu as="div" className="relative">
                        <Menu.Button className="w-full flex justify-between items-center px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-left text-sm focus:outline-none">
                          <span>{filters.status || 'Select Status'}</span>
                          <ChevronDownIcon className="h-4 w-4" />
                        </Menu.Button>
                        <Menu.Items className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-700 shadow-lg rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 focus:outline-none">
                          {statusOptions.map((option) => (
                            <Menu.Item key={option}>
                              {({ active }) => (
                                <button
                                  onClick={() => handleFilterChange('status', option)}
                                  className={`${active ? 'bg-gray-100 dark:bg-gray-600' : ''} block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200`}
                                >
                                  {option}
                                </button>
                              )}
                            </Menu.Item>
                          ))}
                        </Menu.Items>
                      </Menu>
                    </div>

                    {/* Name */}
                    <div className="space-y-1">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Name</label>
                      <input
                        type="text"
                        value={filters.name}
                        onChange={(e) => handleFilterChange('name', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-white dark:bg-gray-700"
                        placeholder="Enter User name"
                      />
                    </div>

                    {/* Wallet Range */}
                    <div className="space-y-1">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Wallet Balance</label>
                      <div className="grid grid-cols-2 gap-2">
                        <input
                          type="number"
                          value={filters.minWallet}
                          onChange={(e) => handleFilterChange('minWallet', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-white dark:bg-gray-700"
                          placeholder="Min"
                        />
                        <input
                          type="number"
                          value={filters.maxWallet}
                          onChange={(e) => handleFilterChange('maxWallet', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-white dark:bg-gray-700"
                          placeholder="Max"
                        />
                      </div>
                    </div>

                    {/* Mobile Number */}
                    <div className="space-y-1">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Mobile Number</label>
                      <input
                        type="text"
                        value={filters.mobileNumber}
                        onChange={(e) => handleFilterChange('mobileNumber', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-white dark:bg-gray-700"
                        placeholder="Enter mobile number"
                      />
                    </div>

                    {/* User ID */}
                    <div className="space-y-1">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">User ID</label>
                      <input
                        type="text"
                        value={filters.UserId}
                        onChange={(e) => handleFilterChange('UserId', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-white dark:bg-gray-700"
                        placeholder="Enter User ID"
                      />
                    </div>

                    {/* eKYC */}
                    <div className="space-y-1">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">eKYC Completed</label>
                      <Menu as="div" className="relative">
                        <Menu.Button className="w-full flex justify-between items-center px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-left text-sm focus:outline-none">
                          <span>{filters.eKYC || 'Select'}</span>
                          <ChevronDownIcon className="h-4 w-4" />
                        </Menu.Button>
                        <Menu.Items className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-700 shadow-lg rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 focus:outline-none">
                          {yesNoOptions.map((option) => (
                            <Menu.Item key={option}>
                              {({ active }) => (
                                <button
                                  onClick={() => handleFilterChange('eKYC', option)}
                                  className={`${active ? 'bg-gray-100 dark:bg-gray-600' : ''} block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200`}
                                >
                                  {option}
                                </button>
                              )}
                            </Menu.Item>
                          ))}
                        </Menu.Items>
                      </Menu>
                    </div>
                  </div>

                  <div className="mt-6 flex space-x-3">
                    <button
                      onClick={resetFilters}
                      className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600"
                    >
                      Reset
                    </button>
                    <button
                      onClick={applyFilters}
                      className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                    >
                      Apply Filters
                    </button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Overlay when filter drawer is open */}
          {filterDrawerOpen && (
            <div
              className="fixed inset-0 bg-black bg-opacity-50 z-40"
              onClick={() => setFilterDrawerOpen(false)}
            />
          )}

          <main className="flex-1 p-6 overflow-auto">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center justify-between mb-6">
                <button
                  onClick={() => navigate(-1)}
                  className="flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-600"
                >
                  <ChevronLeftIcon className="h-5 w-5 mr-1" />
                  Back
                </button>
                <h2 className="text-xl font-semibold">
                  Users Management
                </h2>
                <button
                  onClick={() => setFilterDrawerOpen(true)}
                  className="p-2 rounded-full hover:bg-gray-300 bg-gray-200 dark:hover:bg-gray-700 flex items-center justify-center"
                >
                  <TbFilterPause className="h-5 w-5 text-gray-600 dark:text-gray-300" />
                </button>
              </div>
              {children || (
                <>
                  {/* Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                    {cardData.map((card, index) => (
                      <div key={index} className={`${card.color} rounded-lg shadow p-6 text-white`}>
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="text-sm font-medium opacity-80">{card.title}</p>
                            <h3 className="text-2xl font-bold mt-1">{card.amount}</h3>
                          </div>
                          <div className="p-2 rounded-full bg-white bg-opacity-20">
                            {card.icon}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* User Cards */}
                  <div className="space-y-4">
                    {loading && (
                      <div className="flex justify-center items-center py-12">
                        <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
                      </div>
                    )}

                    {!loading && getPaginatedData().length > 0 ? (
                      <>
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead className="bg-gray-50 dark:bg-gray-700">
                              <tr>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Name</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Mobile</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Balance</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Join Date</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">eKYC</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Refer Code</th>
                                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Actions</th>
                              </tr>
                            </thead>
                            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                              {getPaginatedData().map((user) => (
                                <tr key={user.id}>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm font-medium text-gray-900 dark:text-white">{user.name}</div>
                                    <div className="text-sm text-gray-500 dark:text-gray-400">ID: {user.id}</div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{user.mobile}</td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">₹{user.walletBalance}</td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">{user.joiningDate}</td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <span
                                      className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                  ${user.eKYC === 'Yes'
                                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                          : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                                        }`}
                                    >
                                      {user.eKYC}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">{user.referCode}</td>
                                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium flex items-center gap-1 justify-end">
                                    {/* Status Switch */}
                                    <Switch
                                      checked={user.status === "Active"}
                                      onChange={(enabled) => handleStatusChange(user.id, enabled)}
                                      className={`${user.status === "Active" ? "bg-green-500" : "bg-red-500"
                                        } relative inline-flex h-6 w-11 items-center rounded-full transition-colors`}
                                    >
                                      <span className="sr-only">Toggle status</span>
                                      <span
                                        className={`${user.status === "Active" ? "translate-x-6" : "translate-x-1"
                                          } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                                      />
                                    </Switch>

                                    {/* View Profile */}
                                    <button
                                      onClick={() => window.open(`/profile?userId=${user.id}`, "_blank")}
                                      className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700"
                                      title="View Profile"
                                    >
                                      <UserIcon className="h-5 w-5 text-gray-500 dark:text-gray-300" />
                                    </button>

                                    {/* Transactions */}
                                    <button
                                      onClick={() => window.open(`/order-info?userId=${user.id}`, "_blank")}
                                      className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700"
                                      title="Transactions"
                                    >
                                      <BanknotesIcon className="h-5 w-5 text-gray-500 dark:text-gray-300" />
                                    </button>

                                    {/* Wallet History */}
                                    <button
                                      onClick={() => window.open(`/ledger?userId=${user.id}`, "_blank")}
                                      className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700"
                                      title="Wallet History"
                                    >
                                      <WalletIcon className="h-5 w-5 text-gray-500 dark:text-gray-300" />
                                    </button>
                                  </td>

                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>

                        {/* Pagination */}
                        <div className="flex items-center justify-between px-4 py-3 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
                          <div className="flex-1 flex justify-between sm:hidden">
                            <button
                              onClick={() => handlePageChange(pagination.currentPage - 1)}
                              disabled={pagination.currentPage === 1}
                              className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600"
                            >
                              Previous
                            </button>
                            <button
                              onClick={() => handlePageChange(pagination.currentPage + 1)}
                              disabled={pagination.currentPage === pagination.totalPages}
                              className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600"
                            >
                              Next
                            </button>
                          </div>
                          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                            <div>
                              <p className="text-sm text-gray-700 dark:text-gray-300">
                                Showing <span className="font-medium">{(pagination.currentPage - 1) * pagination.itemsPerPage + 1}</span> to{' '}
                                <span className="font-medium">
                                  {Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems)}
                                </span>{' '}
                                of <span className="font-medium">{pagination.totalItems}</span> results
                              </p>
                            </div>
                            <div>
                              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                                <button
                                  onClick={() => handlePageChange(pagination.currentPage - 1)}
                                  disabled={pagination.currentPage === 1}
                                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300"
                                >
                                  <span className="sr-only">Previous</span>
                                  <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
                                </button>

                                {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                                  let pageNum;
                                  if (pagination.totalPages <= 5) {
                                    pageNum = i + 1;
                                  } else if (pagination.currentPage <= 3) {
                                    pageNum = i + 1;
                                  } else if (pagination.currentPage >= pagination.totalPages - 2) {
                                    pageNum = pagination.totalPages - 4 + i;
                                  } else {
                                    pageNum = pagination.currentPage - 2 + i;
                                  }

                                  return (
                                    <button
                                      key={pageNum}
                                      onClick={() => handlePageChange(pageNum)}
                                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${pagination.currentPage === pageNum
                                        ? 'z-10 bg-blue-50 border-blue-500 text-blue-600 dark:bg-gray-600 dark:text-white'
                                        : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300'
                                        }`}
                                    >
                                      {pageNum}
                                    </button>
                                  );
                                })}

                                <button
                                  onClick={() => handlePageChange(pagination.currentPage + 1)}
                                  disabled={pagination.currentPage === pagination.totalPages}
                                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300"
                                >
                                  <span className="sr-only">Next</span>
                                  <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
                                </button>
                              </nav>
                            </div>
                          </div>
                        </div>
                      </>
                    ) : (
                      !loading && (
                        <div className="text-center py-12">
                          <p className="text-gray-500 dark:text-gray-400">No users found matching your criteria</p>
                        </div>
                      )
                    )}
                  </div>
                </>
              )}
            </div>
          </main>
        </main>
      </div>
      <ToastContainer />
    </div>
  );
};

export default Users;